import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Play, Clock, ChevronRight, ArrowRight, Send, X,
  MessageCircle, Sparkles, BookOpen, Users, Target,
  Zap, Award, TrendingUp, CheckCircle2, Loader2,
  Volume2, VolumeX, Pause, Film, Video, Calendar,
  Check, Bot, FileText, Star, Globe
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

// Lesson visual images
const LESSON_IMAGES = {
  'market': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
  'strategy': 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80',
  'fundraising': 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800&q=80',
  'investor': 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&q=80',
  'product': 'https://images.unsplash.com/photo-1512758017271-d7b84c2113f1?w=800&q=80',
  'technical': 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80',
  'team': 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80',
  'scale': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
  'growth': 'https://images.unsplash.com/photo-1543286386-713bdd548da4?w=800&q=80',
  'default': 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&q=80'
};

const getLessonImage = (title) => {
  const lowerTitle = title?.toLowerCase() || '';
  for (const [keyword, url] of Object.entries(LESSON_IMAGES)) {
    if (lowerTitle.includes(keyword)) return url;
  }
  return LESSON_IMAGES.default;
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
};

const MasterclassPage = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  
  // Main view state
  const [activeTab, setActiveTab] = useState('learn'); // 'learn' or 'cohort'
  const [view, setView] = useState('home'); // home, curriculum, lesson
  
  // AI Generation states
  const [topic, setTopic] = useState('');
  const [syllabus, setSyllabus] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [lessonContent, setLessonContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lessonLoading, setLessonLoading] = useState(false);
  
  // Audio state
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  
  // Video state
  const [videoGenerating, setVideoGenerating] = useState(false);
  // Feature flag: AI video generation. Toggle to false if Universal Key runs out of Sora 2 balance.
  const VIDEO_GEN_ENABLED = true;
  const [lessonVideo, setLessonVideo] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  
  // Chat
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const featuredTopics = [
    { title: "Series A Fundraising", desc: "Navigate down markets" },
    { title: "Product-Market Fit", desc: "Validate before scale" },
    { title: "Technical Moats", desc: "Build defensibility" },
    { title: "Go-to-Market Strategy", desc: "Launch with impact" },
    { title: "Unit Economics", desc: "Master your numbers" },
    { title: "Scaling Operations", desc: "Grow without breaking" }
  ];

  // Generate Syllabus
  const generateSyllabus = async () => {
    if (!topic.trim()) {
      toast({ title: "Enter a topic", description: "What do you want to master?", variant: "destructive" });
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/masterclass/syllabus`, {
        topic, industry: 'Technology', startup_stage: 'Seed', location: 'Global'
      }, token ? { headers } : {});
      
      setSyllabus(res.data);
      setView('curriculum');
      toast({ title: "Your masterclass is ready", description: "Start learning now" });
    } catch (error) {
      toast({ title: "Generation failed", description: "Please try again", variant: "destructive" });
    }
    setLoading(false);
  };

  // Load Lesson
  const loadLesson = async (lesson, lessonIndex = 0) => {
    setCurrentLesson(lesson);
    setCurrentLessonIndex(lessonIndex);
    setLessonContent(null);
    setLessonLoading(true);
    setView('lesson');
    setAudioUrl(null);
    setLessonVideo(null);
    
    try {
      const res = await axios.post(`${API}/api/masterclass/lesson`, {
        syllabus_topic: syllabus?.topic || topic,
        lesson
      }, token ? { headers } : {});
      
      setLessonContent(res.data);
      
      // Check for existing videos for this lesson — week is derived from the
      // lesson's position in the syllabus (1-based) to stay consistent with
      // the module numbering shown in the UI. Avoid parseInt(lesson.id) which
      // returns NaN for non-numeric ids like "l-1", causing every module to
      // resolve to week 1.
      try {
        const videoRes = await axios.get(`${API}/api/masterclass/week-videos`);
        const videos = videoRes.data.videos || [];
        const lessonNum = lessonIndex + 1;
        const existingVideo = videos.find(v => v.week === lessonNum && v.status === 'completed');
        if (existingVideo) {
          setLessonVideo(`${API}${existingVideo.video_url}`);
        }
      } catch (e) {
        // No existing video, that's fine
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to load lesson", variant: "destructive" });
    }
    setLessonLoading(false);
  };

  // Play Audio Narration
  const playNarration = async () => {
    if (!lessonContent) return;
    
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }
    
    if (audioUrl && audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
      return;
    }
    
    setAudioLoading(true);
    try {
      const narrationText = `
        ${lessonContent.summary}. 
        The key strategic insight is: ${lessonContent.axis_of_leverage}. 
        ${lessonContent.pillars?.framework?.substring(0, 400)}. 
        Your action item: ${lessonContent.action_item}
      `.trim();
      
      const res = await axios.post(`${API}/api/masterclass/tts`, {
        text: narrationText,
        voice: 'onyx'
      });
      
      const audioData = res.data.audio;
      const blob = new Blob(
        [Uint8Array.from(atob(audioData), c => c.charCodeAt(0))],
        { type: 'audio/mp3' }
      );
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.play();
        setIsPlaying(true);
      }
      
      toast({ title: "🎧 Playing narration", description: "Listen to your lesson" });
    } catch (error) {
      toast({ title: "Audio unavailable", description: "Could not generate narration", variant: "destructive" });
    }
    setAudioLoading(false);
  };

  // Generate Video for Lesson
  const generateLessonVideo = async () => {
    if (!currentLesson || !lessonContent) return;
    
    setVideoGenerating(true);
    try {
      // Build a topic-specific cinematic prompt from the actual lesson content
      // (not a generic "founder" template) so the video matches the curriculum
      const summarySnippet = (lessonContent.summary || '').slice(0, 220);
      const prompt = `Cinematic 4K shot illustrating "${currentLesson.title}". Editorial documentary style — Dubai / global business setting, golden hour lighting, professional founder presenting. Visual concept: ${summarySnippet}. Slow camera movement, sharp focus, depth of field, luxury magazine feel.`;
      
      const res = await axios.post(`${API}/api/masterclass/generate-video`, {
        prompt,
        week: currentLessonIndex + 1,
        duration: 8  // 8s — balance between richness and cost
      });
      
      toast({ 
        title: "🎬 Video Generation Started", 
        description: "This takes 2-5 minutes. We'll notify you when ready." 
      });
      
      // Poll for completion
      pollVideoStatus(res.data.video_id);
    } catch (error) {
      toast({ title: "Video generation failed", variant: "destructive" });
      setVideoGenerating(false);
    }
  };

  const pollVideoStatus = async (videoId) => {
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/api/masterclass/video-status/${videoId}`);
        
        if (res.data.status === 'completed') {
          setLessonVideo(`${API}${res.data.video_url}`);
          setVideoGenerating(false);
          toast({ title: "🎉 Video Ready!", description: "Click to watch" });
          return;
        } else if (res.data.status === 'failed') {
          setVideoGenerating(false);
          const errMsg = res.data.error_message || 'Please try again in a moment.';
          const isBalance = errMsg.toLowerCase().includes('insufficient balance') || errMsg.toLowerCase().includes('top up');
          toast({
            title: isBalance ? '💳 Universal Key out of balance' : 'Video generation failed',
            description: errMsg,
            variant: 'destructive',
            duration: 8000,
          });
          return;
        }
        
        setTimeout(checkStatus, 5000);
      } catch (error) {
        setVideoGenerating(false);
      }
    };
    
    checkStatus();
  };

  // Send Chat Message
  const sendChat = async () => {
    if (!chatMessage.trim()) return;
    
    setChatLoading(true);
    const msg = chatMessage;
    setChatMessage('');
    setChatHistory(prev => [...prev, { role: 'user', content: msg }]);
    
    try {
      const res = await axios.post(`${API}/api/masterclass/chat`, {
        message: msg, context: syllabus?.topic || topic
      }, token ? { headers } : {});
      
      setChatHistory(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setChatHistory(prev => [...prev, { role: 'assistant', content: 'Unable to respond. Try again.' }]);
    }
    setChatLoading(false);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  useEffect(() => {
    setAudioUrl(null);
    setIsPlaying(false);
    setLessonVideo(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
  }, [currentLesson]);

  // ========== RENDER ==========

  return (
    <div className="min-h-screen bg-[#FAF7F0] text-[#0D1B2A]">
      <Header />
      
      {/* Hidden audio element */}
      <audio 
        ref={audioRef} 
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
      />

      {/* Main Tabs */}
      <div className="container mx-auto px-6 pt-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white/5 border border-[rgba(13,27,42,0.1)] p-1 rounded-xl mb-8">
            <TabsTrigger value="learn" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black rounded-lg px-8 py-3">
              <Sparkles className="h-4 w-4 mr-2" />
              AI Masterclass
            </TabsTrigger>
            <TabsTrigger value="cohort" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black rounded-lg px-8 py-3" data-testid="tab-apply-cohort">
              <Users className="h-4 w-4 mr-2" />
              Apply to Cohort 1
            </TabsTrigger>
          </TabsList>

          {/* AI MASTERCLASS TAB */}
          <TabsContent value="learn">
            {view === 'home' && (
              <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
                {/* Hero */}
                <motion.div variants={fadeInUp} className="text-center mb-16">
                  <p className="text-xs font-medium tracking-[0.3em] uppercase text-amber-500 mb-4">
                    AI-POWERED LEARNING
                  </p>
                  <h1 className="font-serif text-5xl md:text-7xl font-normal tracking-tight mb-6">
                    Learn Anything<br />
                    <span className="text-amber-500">Instantly</span>
                  </h1>
                  <p className="text-xl text-[#3A4A5C] font-light max-w-2xl mx-auto mb-12">
                    Enter any topic and get a personalized masterclass with AI-generated lessons, 
                    voice narration, and video content.
                  </p>
                  
                  {/* Input */}
                  <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto mb-16">
                    <Input
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && generateSyllabus()}
                      placeholder="What do you want to master?"
                      className="flex-1 h-14 bg-white/5 border-[rgba(13,27,42,0.1)] text-[#0D1B2A] placeholder:text-[#0D1B2A]/30 text-lg rounded-full px-6"
                      data-testid="topic-input"
                    />
                    <Button 
                      onClick={generateSyllabus}
                      disabled={loading}
                      className="h-14 px-8 bg-amber-500 hover:bg-amber-400 text-black rounded-full font-semibold"
                      data-testid="generate-btn"
                    >
                      {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Generate'}
                    </Button>
                  </div>
                </motion.div>

                {/* Featured Topics */}
                <motion.div variants={staggerContainer}>
                  <p className="text-xs font-medium tracking-[0.3em] uppercase text-[#3A4A5C]/70 mb-4">
                    Popular Topics
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {featuredTopics.map((t, i) => (
                      <motion.div
                        key={i}
                        variants={fadeInUp}
                        onClick={() => setTopic(t.title)}
                        className="p-6 border border-[rgba(13,27,42,0.08)] hover:border-amber-500/30 rounded-xl cursor-pointer transition-all"
                        data-testid={`topic-${i}`}
                      >
                        <h3 className="text-lg font-medium mb-1">{t.title}</h3>
                        <p className="text-sm text-[#3A4A5C]/70">{t.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* SKI Asia Entrepreneurship Academy — Master Profile */}
                <motion.div variants={staggerContainer} className="mt-32" data-testid="ski-asia-academy">
                  {/* Eyebrow */}
                  <motion.div variants={fadeInUp} className="text-center mb-4">
                    <span className="inline-flex items-center gap-3 text-[11px] font-bold tracking-[0.3em] uppercase text-amber-500">
                      <span className="block w-8 h-px bg-amber-500/60" />
                      Proudly powered by Ski Asia
                      <span className="block w-8 h-px bg-amber-500/60" />
                    </span>
                  </motion.div>

                  {/* Big serif title */}
                  <motion.div variants={fadeInUp} className="text-center mb-6">
                    <h2 className="font-serif text-5xl md:text-6xl font-normal tracking-tight leading-[1.05]">
                      The <em className="italic text-amber-500">Ski Asia</em><br />
                      Entrepreneurship <em className="italic text-amber-500">Academy.</em>
                    </h2>
                  </motion.div>

                  <motion.div variants={fadeInUp} className="text-center mb-12">
                    <p className="font-serif italic text-xl text-[#3A4A5C] max-w-2xl mx-auto leading-relaxed">
                      World-class short, practical, free courses to help you improve your quality of life — and own your destiny.
                    </p>
                  </motion.div>

                  {/* 3 value pills */}
                  <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-3 mb-20">
                    {[
                      { icon: Award, label: 'Full Lifetime Access' },
                      { icon: CheckCircle2, label: 'Truly Free · No Ads, No Subscriptions' },
                      { icon: FileText, label: 'Certificate of Completion' },
                    ].map((p, i) => (
                      <span key={i} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-semibold tracking-wider uppercase" style={{ background: 'rgba(200,168,75,0.07)', border: '1px solid rgba(200,168,75,0.25)', color: '#F0E8D4' }}>
                        <p.icon className="h-3.5 w-3.5" style={{ color: '#C8A84B' }} />
                        {p.label}
                      </span>
                    ))}
                  </motion.div>

                  {/* Featured popular masterclasses */}
                  <motion.div variants={fadeInUp} className="mb-20">
                    <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                      <div>
                        <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-amber-500 mb-2">Popular masterclasses</p>
                        <h3 className="font-serif text-3xl md:text-4xl font-normal tracking-tight">Pick one to <em className="italic text-amber-500">begin.</em></h3>
                      </div>
                      <a href="https://www.thestartuptribe.org/pages/ski-asia" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold tracking-[0.18em] uppercase text-amber-500 hover:text-amber-400 inline-flex items-center gap-2" data-testid="academy-all-courses">
                        Browse all courses <ArrowRight className="h-3 w-3" />
                      </a>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[
                        { title: 'The 14 Day Startup Challenge', cat: 'Aspiring Founders', rating: 4.8, reviews: 148, lessons: 16, slug: 'aspiring-entrepreneurs-the-14-day-entrepreneurship-challenge' },
                        { title: 'Understanding the Entrepreneurial Lifecycle', cat: 'Strategy', rating: 4.8, reviews: 96, lessons: 1, slug: 'entrepreneurial-lifecycle' },
                        { title: 'How to Find Clients & Grow Your Business', cat: 'Sales · Growth', rating: 4.9, reviews: 47, lessons: 46, slug: 'how-to-find-clients' },
                        { title: 'How to Start a Farming Business', cat: 'Agriculture', rating: 4.8, reviews: 115, lessons: 24, slug: 'start-a-farm' },
                        { title: 'Understanding Money & How to Handle It', cat: 'Finance', rating: 4.9, reviews: 20, lessons: 18, slug: 'money' },
                        { title: 'How to Find Investors — A Reality Check', cat: 'Fundraising', rating: 5.0, reviews: 9, lessons: 10, slug: 'funding' },
                        { title: 'First Aid', cat: 'Healthcare', rating: 4.9, reviews: 13, lessons: 102, slug: 'firstaid' },
                        { title: 'How to Discover Your Personal Value Proposition', cat: 'Self-Knowledge', rating: 4.6, reviews: 24, lessons: 15, slug: 'personal-value-proposition' },
                        { title: 'Sustainable Entrepreneurship', cat: 'Sustainability', rating: 4.7, reviews: 12, lessons: 11, slug: 'sustainableentrepreneurship' },
                      ].map((c, i) => (
                        <a
                          key={i}
                          href={`https://www.thestartuptribe.org/courses/${c.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group p-6 rounded-xl transition-all relative overflow-hidden"
                          style={{ background: '#FFFFFF', border: '1px solid rgba(13,27,42,0.08)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(200,168,75,0.35)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'none'; }}
                          data-testid={`academy-course-${i}`}
                        >
                          <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-amber-500 mb-3">{c.cat}</p>
                          <h4 className="font-serif text-xl leading-snug text-[#0D1B2A] mb-4 group-hover:text-amber-500 transition-colors">{c.title}</h4>
                          <div className="flex items-center justify-between text-xs text-[#3A4A5C]/80">
                            <span className="flex items-center gap-1.5">
                              <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                              <span className="text-[#0D1B2A] font-semibold">{c.rating}</span>
                              <span>({c.reviews} reviews)</span>
                            </span>
                            <span className="flex items-center gap-1.5">
                              <Play className="h-3 w-3" />
                              {c.lessons} {c.lessons === 1 ? 'module' : 'lessons'}
                            </span>
                          </div>
                          <div className="mt-4 inline-flex items-center text-[11px] font-bold tracking-[0.18em] uppercase text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity">
                            Enroll free <ArrowRight className="h-3 w-3 ml-1" />
                          </div>
                        </a>
                      ))}
                    </div>
                  </motion.div>

                  {/* Faculty */}
                  <motion.div variants={fadeInUp} className="mb-12">
                    <div className="text-center mb-10">
                      <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-amber-500 mb-3">Meet the faculty</p>
                      <h3 className="font-serif text-3xl md:text-4xl font-normal tracking-tight">Experts <em className="italic text-amber-500">sharing their knowledge.</em></h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {[
                        { name: 'Mike Michalowicz', teaches: 'Eradicating entrepreneurial poverty' },
                        { name: 'Philippa Geard', teaches: 'Working in the SME sector' },
                        { name: 'Mia Jurgens', teaches: 'Creating safe spaces for children' },
                        { name: 'Meg Mateer', teaches: 'Dealing with difficult times' },
                        { name: 'Dr Kent Gustavson', teaches: 'Personal value proposition' },
                        { name: 'Dineo Tsamela', teaches: 'Understanding & handling money' },
                        { name: 'Clive Butkow', teaches: 'Finding investors — a reality check' },
                        { name: 'Prof Adrian Saville', teaches: 'Why the economy matters' },
                        { name: 'Paula Quinsee', teaches: 'Workplace harassment — what to do' },
                        { name: 'Kalnisha Singh', teaches: 'Sustainable entrepreneurship' },
                        { name: 'Regine Harr', teaches: 'Finding your first 10 clients' },
                        { name: 'Lynda Smith', teaches: 'Thriving in the second half of life' },
                      ].map((f, i) => {
                        const initials = f.name.split(' ').filter(p => !p.includes('.')).slice(-2).map(p => p[0]).join('');
                        return (
                          <div key={i} className="p-5 rounded-xl flex items-start gap-3" style={{ background: '#FFFFFF', border: '1px solid rgba(13,27,42,0.06)' }} data-testid={`faculty-${i}`}>
                            <div className="w-12 h-12 rounded-full flex items-center justify-center font-serif text-base font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg, #C8A84B 0%, #D9BC6A 100%)', color: '#07070A', letterSpacing: '.04em' }}>
                              {initials}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-[#0D1B2A] leading-tight">{f.name}</p>
                              <p className="text-xs text-[#3A4A5C]/80 mt-1.5 leading-snug">Teaches {f.teaches}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>

                  {/* Bottom CTA */}
                  <motion.div variants={fadeInUp} className="text-center py-12 px-8 rounded-2xl mb-8" style={{ background: 'linear-gradient(135deg, rgba(200,168,75,0.08) 0%, rgba(200,168,75,0.02) 100%)', border: '1px solid rgba(200,168,75,0.25)' }}>
                    <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-amber-500 mb-3">Please never ever forget…</p>
                    <h3 className="font-serif text-3xl md:text-5xl font-normal tracking-tight mb-4">
                      How <em className="italic text-amber-500">absolutely awesome</em> you truly are.
                    </h3>
                    <p className="font-serif italic text-lg text-[#3A4A5C] max-w-xl mx-auto">
                      Just the fact that you are here looking at opportunities to grow says so much about you.
                    </p>
                  </motion.div>
                </motion.div>
              </motion.div>
            )}

            {view === 'curriculum' && syllabus && (
              <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
                {/* Back Button */}
                <button 
                  onClick={() => setView('home')}
                  className="text-[#3A4A5C]/70 hover:text-[#0D1B2A] text-sm flex items-center gap-2 mb-8"
                >
                  <ChevronRight className="h-4 w-4 rotate-180" />
                  Back
                </button>

                {/* Header */}
                <motion.div variants={fadeInUp} className="mb-12">
                  <p className="text-xs font-medium tracking-[0.3em] uppercase text-amber-500 mb-4">
                    Your Masterclass
                  </p>
                  <h1 className="font-serif text-4xl md:text-5xl font-normal tracking-tight mb-4">
                    {syllabus.topic}
                  </h1>
                  <p className="text-lg text-[#3A4A5C]/80 max-w-2xl">{syllabus.description}</p>
                  <div className="flex gap-6 mt-4 text-sm text-[#3A4A5C]/70">
                    <span className="flex items-center gap-2"><BookOpen className="h-4 w-4" />{syllabus.lessons?.length || 6} Modules</span>
                    <span className="flex items-center gap-2"><Clock className="h-4 w-4" />~4 hours</span>
                    <span className="flex items-center gap-2"><Volume2 className="h-4 w-4" />Audio + Video</span>
                  </div>
                </motion.div>

                {/* Modules Grid */}
                <motion.div variants={staggerContainer} className="grid md:grid-cols-2 gap-4">
                  {syllabus.lessons?.map((lesson, i) => (
                    <motion.div
                      key={lesson.id}
                      variants={fadeInUp}
                      onClick={() => loadLesson(lesson, i)}
                      className="group p-6 bg-white border border-[rgba(13,27,42,0.08)] hover:border-amber-500/30 rounded-xl cursor-pointer transition-all"
                      data-testid={`lesson-${i}`}
                    >
                      <div className="flex items-start gap-4">
                        <span className="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 text-[#3A4A5C] font-mono group-hover:bg-amber-500 group-hover:text-black transition-all">
                          {i + 1}
                        </span>
                        <div className="flex-1">
                          <h3 className="text-lg font-medium mb-1 group-hover:text-amber-500 transition-colors">
                            {lesson.title}
                          </h3>
                          <p className="text-sm text-[#3A4A5C]/70 line-clamp-2">{lesson.description}</p>
                          <p className="text-xs text-[#0D1B2A]/30 mt-2 flex items-center gap-1">
                            <Clock className="h-3 w-3" />{lesson.duration}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            )}

            {view === 'lesson' && (
              <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
                {/* Back Button */}
                <button 
                  onClick={() => setView('curriculum')}
                  className="text-[#3A4A5C]/70 hover:text-[#0D1B2A] text-sm flex items-center gap-2 mb-8"
                >
                  <ChevronRight className="h-4 w-4 rotate-180" />
                  Back to Curriculum
                </button>

                {lessonLoading ? (
                  <div className="flex flex-col items-center justify-center h-[50vh]">
                    <div className="w-16 h-16 border-2 border-amber-500 rounded-full animate-spin border-t-transparent" />
                    <p className="mt-6 text-[#3A4A5C]/70">Generating lesson...</p>
                  </div>
                ) : lessonContent && currentLesson && (
                  <>
                    {/* Hero with Image/Video */}
                    <motion.div variants={fadeInUp} className="relative h-64 md:h-80 rounded-2xl overflow-hidden mb-8">
                      {lessonVideo && showVideo ? (
                        <video 
                          src={lessonVideo} 
                          controls 
                          autoPlay 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img 
                          src={getLessonImage(currentLesson.title)} 
                          alt={currentLesson.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
                      
                      {/* Title Overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-8">
                        <p className="text-xs font-medium tracking-[0.3em] uppercase text-amber-500 mb-2">
                          Module {currentLesson.id}
                        </p>
                        <h1 className="font-serif text-3xl md:text-4xl font-normal">
                          {currentLesson.title}
                        </h1>
                      </div>
                      
                      {/* Media Controls */}
                      <div className="absolute top-6 right-6 flex gap-3">
                        {/* Audio Button */}
                        <button
                          onClick={playNarration}
                          disabled={audioLoading}
                          className="w-12 h-12 bg-amber-500 hover:bg-amber-400 rounded-full flex items-center justify-center shadow-lg"
                          data-testid="audio-btn"
                        >
                          {audioLoading ? (
                            <Loader2 className="h-5 w-5 text-black animate-spin" />
                          ) : isPlaying ? (
                            <Pause className="h-5 w-5 text-black" />
                          ) : (
                            <Volume2 className="h-5 w-5 text-black" />
                          )}
                        </button>
                        
                        {/* Video Button — disabled while Universal Key is out of Sora 2 balance */}
                        {lessonVideo ? (
                          <button
                            onClick={() => setShowVideo(!showVideo)}
                            className="w-12 h-12 bg-red-500 hover:bg-red-400 rounded-full flex items-center justify-center shadow-lg"
                            data-testid="watch-video-btn"
                          >
                            <Play className="h-5 w-5 text-[#0D1B2A]" />
                          </button>
                        ) : null}
                      </div>
                    </motion.div>

                    {/* Now Playing Indicator */}
                    {isPlaying && (
                      <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-6 text-amber-500">
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(i => (
                            <div 
                              key={i} 
                              className="w-1 bg-amber-500 rounded-full animate-pulse"
                              style={{ height: `${8 + Math.random() * 12}px`, animationDelay: `${i * 0.1}s` }}
                            />
                          ))}
                        </div>
                        <span className="text-sm">Now playing audio narration</span>
                      </motion.div>
                    )}

                    {/* Video Section — only show the generation card if feature is enabled, OR show player if already generated */}
                    <motion.div variants={fadeInUp} className="mb-8">
                      {lessonVideo ? (
                        <div className="rounded-2xl overflow-hidden bg-black border border-[rgba(13,27,42,0.1)]">
                          <div className="aspect-video">
                            <video 
                              src={lessonVideo} 
                              controls 
                              autoPlay
                              playsInline
                              className="w-full h-full"
                              poster={getLessonImage(currentLesson.title)}
                              onError={(e) => {
                                console.error('Video error:', e);
                              }}
                            >
                              Your browser does not support video playback.
                            </video>
                          </div>
                          <div className="flex items-center justify-between px-4 py-3 bg-black/50">
                            <p className="text-[#3A4A5C]/70 text-sm flex items-center gap-2">
                              <Film className="h-4 w-4" />
                              AI-Generated Video • Powered by Sora 2
                            </p>
                            <a 
                              href={lessonVideo} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-amber-500 text-sm hover:underline"
                            >
                              Open in new tab ↗
                            </a>
                          </div>
                        </div>
                      ) : VIDEO_GEN_ENABLED ? (
                        <div className="p-6 border border-dashed border-[rgba(13,27,42,0.15)] rounded-2xl bg-white/[0.02]">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                                <Film className="h-6 w-6 text-red-500" />
                              </div>
                              <div>
                                <p className="font-medium text-[#0D1B2A]">AI Video</p>
                                <p className="text-sm text-[#3A4A5C]/80">
                                  {videoGenerating ? 'Generating video... (1-2 minutes)' : 'Generate a video for this lesson'}
                                </p>
                              </div>
                            </div>
                            <Button
                              onClick={generateLessonVideo}
                              disabled={videoGenerating}
                              className={videoGenerating 
                                ? "bg-white/10 text-[#0D1B2A]" 
                                : "bg-red-500 hover:bg-red-400 text-[#0D1B2A]"
                              }
                              data-testid="generate-video-main-btn"
                            >
                              {videoGenerating ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Play className="h-4 w-4 mr-2" />
                                  Generate Video
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </motion.div>

                    {/* Summary */}
                    <motion.div variants={fadeInUp} className="mb-8">
                      <p className="text-xl text-[#0D1B2A]/70 font-light leading-relaxed">
                        {lessonContent.summary}
                      </p>
                    </motion.div>

                    {/* Key Insight */}
                    {lessonContent.axis_of_leverage && (
                      <motion.div variants={fadeInUp} className="mb-12 p-8 bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 rounded-2xl">
                        <p className="text-xs font-medium tracking-[0.3em] uppercase text-amber-500 mb-4">
                          Key Strategic Insight
                        </p>
                        <p className="text-2xl font-light leading-relaxed">
                          "{lessonContent.axis_of_leverage}"
                        </p>
                      </motion.div>
                    )}

                    {/* Four Pillars */}
                    <motion.div variants={staggerContainer} className="grid md:grid-cols-2 gap-4 mb-12">
                      {[
                        { key: 'framework', title: 'Framework', icon: Target },
                        { key: 'execution', title: 'Execution', icon: Zap },
                        { key: 'technical', title: 'Technical', icon: TrendingUp },
                        { key: 'risks', title: 'Risk Mitigation', icon: Award },
                      ].map((pillar) => (
                        <motion.div
                          key={pillar.key}
                          variants={fadeInUp}
                          className="p-6 bg-white border border-[rgba(13,27,42,0.08)] rounded-xl"
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <pillar.icon className="h-5 w-5 text-amber-500" />
                            <h3 className="font-medium">{pillar.title}</h3>
                          </div>
                          <p className="text-sm text-[#3A4A5C]/80 leading-relaxed">
                            {lessonContent.pillars?.[pillar.key] || 'Content loading...'}
                          </p>
                        </motion.div>
                      ))}
                    </motion.div>

                    {/* Action Item */}
                    {lessonContent.action_item && (
                      <motion.div variants={fadeInUp} className="p-6 border border-green-500/30 bg-green-500/5 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                          <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#3A4A5C]/70">
                            This Week's Action
                          </p>
                        </div>
                        <p className="text-lg">{lessonContent.action_item}</p>
                      </motion.div>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </TabsContent>

          {/* COHORT TAB */}
          <TabsContent value="cohort">
            <CohortSection />
          </TabsContent>
        </Tabs>
      </div>

      {/* Chat Widget */}
      <ChatWidget 
        open={chatOpen}
        setOpen={setChatOpen}
        chatHistory={chatHistory}
        chatMessage={chatMessage}
        setChatMessage={setChatMessage}
        sendChat={sendChat}
        chatLoading={chatLoading}
      />
    </div>
  );
};

// Cohort Section — points to the real Cohort 1 (AED 6,900, 12-week, 1 Jul 2026)
const CohortSection = () => {
  const navigate = useNavigate();

  return (
    <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-4xl mx-auto" data-testid="cohort-section">
      {/* Hero */}
      <motion.div variants={fadeInUp} className="text-center mb-12">
        <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/30 mb-6">
          COHORT 1 · STARTS 1 JULY 2026 · 20 SEATS
        </Badge>
        <h1 className="font-serif text-5xl font-normal tracking-tight mb-4">
          SKI Axis<br />
          <span className="text-amber-500">Accelerator · Cohort 1</span>
        </h1>
        <p className="text-xl text-[#3A4A5C] mb-8 max-w-2xl mx-auto">
          12-week governance-first programme. 20 founders. Five mentors. One framework that changes everything.
        </p>
        <div className="flex flex-wrap justify-center gap-6 text-sm text-[#3A4A5C]">
          <span><Users className="inline h-4 w-4 mr-1" />20 founders only</span>
          <span><Calendar className="inline h-4 w-4 mr-1" />12 weeks · 1 Jul → 12 Aug 2026</span>
          <span><Zap className="inline h-4 w-4 mr-1" />Apply by 30 June 2026</span>
        </div>
      </motion.div>

      {/* Pricing Card — real numbers */}
      <motion.div variants={fadeInUp} className="bg-gradient-to-b from-amber-500/10 to-transparent border border-amber-500/30 rounded-3xl p-8 md:p-12 mb-12">
        <div className="text-center mb-8">
          <p className="text-[#3A4A5C]/70 text-xs tracking-[0.2em] uppercase mb-2">Programme Fee</p>
          <p className="text-5xl font-bold text-[#0D1B2A]">AED 6,900</p>
          <p className="text-sm text-[#3A4A5C]/70 mt-2 italic">Five fully-funded scholarship seats per cohort under the She Built This × SKI Axis partnership.</p>
        </div>

        <div className="space-y-3 mb-10">
          {[
            "12-week governance-first accelerator (1 Jul → 12 Aug 2026)",
            "Full programme tuition + IFZA company registration",
            "Sentinel governance audit + 3-layer architecture deliverable",
            "Weekly 1:1 with 5 mentors · Neha · Ross · Shanshan · Abu · Shir",
            "Demo Day with UAE family offices + sovereign capital",
            "Microsoft / Google / AWS startup credits unlocked",
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-3">
              <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-[#0D1B2A]">{f}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button
            onClick={() => navigate('/cohort-1/apply')}
            className="h-14 bg-amber-500 hover:bg-amber-400 text-black font-bold text-base rounded-xl"
            data-testid="cohort-apply-cta"
          >
            Start Cohort 1 Application →
          </Button>
          <Button
            onClick={() => navigate('/accelerator')}
            variant="outline"
            className="h-14 border-[rgba(13,27,42,0.3)] text-[#0D1B2A] hover:bg-[rgba(13,27,42,0.05)] font-bold text-base rounded-xl"
            data-testid="cohort-read-more"
          >
            Read the Full Brief
          </Button>
        </div>

        <p className="text-center text-xs text-[#3A4A5C]/70 mt-6 italic">
          Already a paid webinar attendee? Your <strong>Stack the Credits</strong> bonus auto-applies — up to AED 1,000 off Cohort 1.
        </p>
      </motion.div>
    </motion.div>
  );
};

// Chat Widget Component
const ChatWidget = ({ open, setOpen, chatHistory, chatMessage, setChatMessage, sendChat, chatLoading }) => {
  return (
    <>
      <motion.button
        onClick={() => setOpen(!open)}
        className="fixed bottom-8 right-8 z-50 w-14 h-14 bg-amber-500 hover:bg-amber-400 text-black rounded-full flex items-center justify-center shadow-xl"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-28 right-8 z-50 w-96 max-w-[calc(100vw-4rem)] bg-black/95 backdrop-blur-xl border border-[rgba(13,27,42,0.1)] rounded-2xl overflow-hidden shadow-2xl"
          >
            <div className="p-4 border-b border-[rgba(13,27,42,0.1)]">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-black" />
                </div>
                <div>
                  <p className="font-medium text-sm">AI Advisor</p>
                  <p className="text-xs text-[#3A4A5C]/70">Ask anything</p>
                </div>
              </div>
            </div>

            <div className="h-72 overflow-y-auto p-4 space-y-3">
              {chatHistory.length === 0 && (
                <p className="text-center text-[#0D1B2A]/30 text-sm py-8">Start a conversation</p>
              )}
              {chatHistory.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-4 py-2 rounded-2xl text-sm ${
                    msg.role === 'user' ? 'bg-amber-500 text-black' : 'bg-white/5 text-[#0D1B2A]/80'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-white/5 px-4 py-2 rounded-2xl">
                    <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[rgba(13,27,42,0.1)]">
              <div className="flex gap-2">
                <Input
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendChat()}
                  placeholder="Ask a question..."
                  className="flex-1 bg-white/5 border-[rgba(13,27,42,0.1)] text-[#0D1B2A] rounded-xl"
                />
                <Button onClick={sendChat} disabled={chatLoading} className="bg-amber-500 hover:bg-amber-400 text-black rounded-xl">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MasterclassPage;
